import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import ActionsPanel from '../shared/ActionsPanel';
import Modal from '../shared/Modal';
import FileUpload from '../shared/FileUpload';
import {
  BidForm, OutcomeForm, Pill, bidUrgency, GoneQuietBadge, LongWaitBadge, ValueCell, fmtTouch, FollowUpBadge,
  STATUS_COLORS, RESULT_COLORS, CAUSES, SUMMARY_MAX,
  ACTIVE_STAGES, OUTCOME_STATUSES, HideValuesContext, useSavedView, VIEW_PAGE,
} from './BidTrackerPage';
import { actionOwnerId, isLead, LEAD_STEPS } from '../../utils/bidTracker';
import { maskMoney } from '../../utils/bidTracker';
import { fmtDeadlineWithTime, formatValueGbp } from '../../utils/bidValue';
import { fmtDateLong } from '../../utils/bidReportExport';
import BidMergeDialog from './BidMergeDialog';
import BidHardware from './BidHardware';
import SurveysPanel from '../delivery/SurveysPanel';
import { canAccess } from '../../utils/rbac';

// A bare date (an entry logged as "the 20th") shows as a date; a datetime
// shows with its time in the viewer's clock.
const fmtWhen = (s) => {
  if (!s) return '';
  const t = String(s);
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return fmtDay(t);
  const d = new Date(t.includes('T') ? t : t.replace(' ', 'T') + 'Z');
  if (isNaN(d)) return t;
  const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `${fmtDay(local)}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
// Through fmtDeadlineWithTime's fixed month table: toLocaleDateString gives
// "Sept" in some browsers and "Sep" in others.
const fmtDay = (s) => { if (!s) return ''; const v = String(s).slice(0, 10); return /^\d{4}-\d{2}-\d{2}$/.test(v) ? fmtDeadlineWithTime(v) : s; };
// Today as YYYY-MM-DD in UTC, the form the API takes for dates.
const todayIso = () => new Date().toISOString().slice(0, 10);
const USED_FOR_LABEL = { notice: 'Notice', submission: 'Submission', both: 'Notice and submission' };
// The kinds of entry in the interaction log, in the order the composer offers
// them, each with what posting it updates (src/api/bidLog.js does the updating).
// Log was folded into Note by migration 0407: they meant the same thing.
const KINDS = [
  { key: 'call', label: 'Call', icon: '📞', hint: 'Adds a dated call to the timeline and moves Last touch.' },
  { key: 'email', label: 'Email', icon: '✉️', hint: 'Adds an email to the timeline and moves Last touch.' },
  { key: 'portal', label: 'Portal message', icon: '🖥️', hint: 'Also sets Last checked to this date, which clears the portal reminder.' },
  { key: 'visit', label: 'Site visit', icon: '📍', hint: 'Adds a site visit to the timeline and moves Last touch.' },
  { key: 'clarification', label: 'Clarification', icon: '❓', hint: 'Records a clarification question or answer and moves Last touch.' },
  { key: 'chase', label: 'Chase', icon: '🔔', hint: 'Also records the chase, which resets the monthly chase reminder while Awaiting decision.' },
  { key: 'note', label: 'Note', icon: '📝', hint: 'Internal work or a note for the team (drafted a question, agreed pricing). Moves Last touch.' },
];
const kindOf = (k) => KINDS.find(x => x.key === (k === 'log' ? 'note' : k)) || { key: k, label: k, icon: '📝', hint: '' };
// The limit on a next action, as src/api/bidStreamline.js enforces it.
const NEXT_ACTION_MAX = 200;
// A next action that reads as sending something is most likely done by email.
const kindForDone = (text) => (/\b(e-?mail|send|sent|reply|chase)\b/i.test(String(text || '')) ? 'email' : 'note');
const card = 'bg-white border border-gray-200 rounded-xl p-3.5';
const h = 'text-[10.5px] font-bold text-gray-500 uppercase tracking-wider mb-2';
const btnSm = 'px-2.5 py-1 text-[11.5px] font-semibold border border-gray-200 text-gray-700 bg-white rounded-md hover:border-gold disabled:opacity-50';
const btnNavy = 'px-3 py-1 bg-navy text-white text-[12px] font-semibold rounded-md hover:bg-navy/80 disabled:opacity-50';

// The bid record (the CRM redesign of 29 September 2026). A header card with
// what the bid is and what can be done to it; the stepper from Upcoming to Won;
// the stage line pinned beneath it, carrying the standing summary and the next
// action; then the timeline, actions, documents, compliance matrix and hardware
// on the left, and the record fields and platforms on the right.
// Routed as page 'bid-detail'; detailSub 'action-<id>' deep-links an action.
const STEPS = ['Upcoming', ...ACTIVE_STAGES, 'Won'];
// Actions sit inside Timeline since 25 October 2026: both are what is
// happening on the bid, and one tab fewer is one place fewer to look.
const RECORD_TABS = [
  { key: 'timeline', label: 'Timeline' },
  { key: 'documents', label: 'Documents' },
  { key: 'matrix', label: 'Compliance matrix' },
  { key: 'hardware', label: 'Hardware' },
  // Site surveys before the win, for those who hold the Surveys page.
  { key: 'surveys', label: 'Surveys' },
];

export default function BidDetailPage() {
  const { detailId, detailSub, navigate, currentUser } = useApp();
  const isAdmin = (currentUser?.user_role || '').split(',').map(r => r.trim()).includes('admin');
  const targetActionId = detailSub && detailSub.startsWith('action-') ? detailSub.slice('action-'.length) : null;
  const [view] = useSavedView(VIEW_PAGE);

  const [bid, setBid] = useState(null);
  const [people, setPeople] = useState([]);
  const [clients, setClients] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  // null = closed; 'Won' | 'Lost' | 'No-bid' = the outcome form opened for
  // that move; 'open' = a plain "Record outcome" with the result left to pick.
  const [outcome, setOutcome] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [folder, setFolder] = useState(null);       // { exists, webUrl } | null
  const [folderBusy, setFolderBusy] = useState(false);
  const [folderErr, setFolderErr] = useState('');
  const [docs, setDocs] = useState([]);
  const [addingDoc, setAddingDoc] = useState(false);
  const [movingStage, setMovingStage] = useState(false);
  const [moveErr, setMoveErr] = useState('');   // a refused move, shown above the stage line
  const [checkErr, setCheckErr] = useState(''); // a refused portal check, shown by its button
  const [checking, setChecking] = useState(false);
  const [merging, setMerging] = useState(false);
  const [converting, setConverting] = useState(false);
  const [editingContacts, setEditingContacts] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [recordTab, setRecordTab] = useState('timeline');
  const [actionsOpen, setActionsOpen] = useState(!!targetActionId);
  const [requirements, setRequirements] = useState({ rows: [], progress: { total: 0, done: 0, waived: 0, todo: 0 } });
  // What the move to Won did with the bid's hardware, shown on the Hardware tab.
  const [hardwareNotice, setHardwareNotice] = useState(null);

  const loadBid = useCallback(() => {
    if (!detailId) return;
    api.get('crm/bids/' + detailId).then(setBid).catch(() => setBid(null)).finally(() => setLoading(false));
  }, [detailId]);
  const loadTimeline = useCallback(() => {
    if (!detailId) return;
    api.get(`crm/bids/${detailId}/timeline`).then(d => setTimeline(d?.rows || [])).catch(() => setTimeline([]));
  }, [detailId]);
  const loadDocs = useCallback(() => {
    if (!detailId) return;
    api.get(`crm/bids/${detailId}/documents`).then(d => setDocs(Array.isArray(d) ? d : [])).catch(() => setDocs([]));
  }, [detailId]);
  const loadRequirements = useCallback(() => {
    if (!detailId) return;
    api.get(`crm/bids/${detailId}/requirements`).then(d => setRequirements({ rows: d?.rows || [], progress: d?.progress || { total: 0, done: 0, waived: 0, todo: 0 } })).catch(() => {});
  }, [detailId]);
  const refresh = useCallback(() => { loadBid(); loadTimeline(); loadRequirements(); }, [loadBid, loadTimeline, loadRequirements]);

  // The More menu closes on a click anywhere else.
  useEffect(() => {
    if (!moreOpen) return undefined;
    const close = (e) => { if (!e.target.closest('[data-more-menu]')) setMoreOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [moreOpen]);

  useEffect(() => {
    setLoading(true);
    loadBid(); loadTimeline(); loadDocs(); loadRequirements();
    api.get('people').then(d => setPeople(Array.isArray(d) ? d : (d?.rows || []))).catch(() => {});
    api.get('clients').then(d => setClients(Array.isArray(d) ? d : (d?.clients || d?.rows || []))).catch(() => {});
    api.get('crm/columns').then(d => setColumns(d?.rows || [])).catch(() => {});
    api.get(`crm/bids/${detailId}/sharepoint-folder`).then(setFolder).catch(() => setFolder(null));
  }, [detailId, loadBid, loadTimeline, loadDocs, loadRequirements]);

  const moveTo = async (status, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setMovingStage(true); setMoveErr('');
    try {
      await api.put(`crm/bids/${detailId}`, { status, previous_status: bid.status });
      refresh();
    } catch (e) {
      // The API's own words (the matrix is not complete, a legacy bid
      // needing an owner and a deadline) go in amber
      // above the stage line, just under the stepper that was pressed.
      setMoveErr(e?.message || 'Could not update this bid\'s status.');
    } finally { setMovingStage(false); }
  };
  const reopen = () => moveTo(bid.previous_status || 'Evaluating', `Reopen this bid back to "${bid.previous_status || 'Evaluating'}"?`);
  // A stage on the stepper. Won needs a result date, so it opens the outcome
  // form; going back a stage asks first.
  const step = (s) => {
    if (s === bid.status || movingStage) return;
    const from = steps.indexOf(bid.status), to = steps.indexOf(s);
    return s === 'Won' ? setOutcome('Won') : moveTo(s, from !== -1 && to < from ? `Move this ${noun} back to "${s}"?` : undefined);
  };

  // "Checked portal today": the common case of looking and finding nothing,
  // in one click. It is a portal entry, so it stamps last_checked_at and adds
  // a timeline entry.
  const checkedPortal = async () => {
    setChecking(true); setCheckErr('');
    try { await api.post(`crm/bids/${detailId}/interactions`, { kind: 'portal', summary: 'Portal checked, no new messages', occurred_at: todayIso() }); refresh(); }
    catch (ex) { setCheckErr(ex?.message || 'Could not record the check.'); }
    finally { setChecking(false); }
  };

  const createFolder = async () => {
    setFolderBusy(true); setFolderErr('');
    try {
      const r = await api.post(`crm/bids/${detailId}/sharepoint-folder`, {});
      setFolder({ exists: true, webUrl: r.webUrl });
      if (r?.webUrl) window.open(r.webUrl, '_blank', 'noopener');
    } catch (e) {
      setFolderErr(e?.message || 'Could not create the folder.');
    } finally { setFolderBusy(false); }
  };

  const remove = async () => {
    setMoreOpen(false);
    if (!window.confirm(`Delete "${bid.opportunity}"? This cannot be undone.`)) return;
    try { await api.delete('crm/bids/' + bid.id); navigate('bid-tracker'); }
    catch (e) { setMoveErr(e?.message || 'Could not delete this bid.'); }
  };

  const personName = (id) => { const p = people.find(x => x.id === id); return p ? `${p.first_name || ''} ${p.last_name || ''}`.trim() : ''; };
  const ownerName = bid && (personName(bid.owner_id) || bid.owner);
  const causes = bid ? CAUSES.filter(c => bid[c.key]).map(c => c.short) : [];
  // A lead has no deadline to be urgent about: its follow-up badge says when.
  const urg = bid && !isLead(bid) ? bidUrgency(bid) : null;
  // A bid merged into another is kept for its history but is no longer the
  // record anyone works on: its edit and workflow controls go, and a banner
  // points at the survivor.
  const readOnly = !!(bid && bid.merged_into);
  // A lead (migration 0417) is not a tender: it has its own shorter stepper,
  // no compliance matrix, portals or deadline, and can be converted to a
  // tender when a formal ITT arrives.
  const lead = isLead(bid);
  const noun = lead ? 'lead' : 'bid';
  const steps = lead ? LEAD_STEPS : STEPS;
  const mergedFrom = bid && Array.isArray(bid.merged_from) ? bid.merged_from : [];
  const contacts = bid && Array.isArray(bid.contacts) ? bid.contacts : [];
  const primary = contacts.find(c => c.is_primary) || null;

  if (loading) return <div className="p-6 text-xs text-gray-500">Loading…</div>;
  if (!bid) return (
    <div className="p-6 text-xs text-gray-500">
      Bid not found. <button onClick={() => navigate('bid-tracker')} className="text-navy underline">Back to Pipeline Tracker</button>
    </div>
  );

  const decided = OUTCOME_STATUSES.includes(bid.status);
  const submitted = ['Responded', 'Awaiting decision', 'In Discussion'].includes(bid.status) || decided;
  const platform = Array.isArray(bid.platforms) && bid.platforms.length ? bid.platforms[0].platform : null;
  const custom = (() => { try { return bid.custom_json ? JSON.parse(bid.custom_json) : {}; } catch { return {}; } })();
  const stepIdx = steps.indexOf(bid.status);
  const outcomeLine = [
    bid.result ? `${bid.result}${bid.result_date ? ` on ${fmtDeadlineWithTime(bid.result_date)}` : ''}.` : bid.status === 'No-bid' ? `No-bid${bid.result_date ? ` on ${fmtDeadlineWithTime(bid.result_date)}` : ''}.` : `${bid.status}.`,
    bid.no_bid_reason ? `Reason: ${bid.no_bid_reason}.` : '',
    bid.winning_supplier || bid.winning_price_gbp != null ? `Winning bid: ${bid.winning_supplier || 'supplier not recorded'}${bid.winning_price_gbp != null ? ` at ${maskMoney(formatValueGbp(bid.winning_price_gbp), !!view.hideValues)}` : ''}.` : '',
    causes.length ? `Root causes: ${causes.join(', ')}.` : '',
    bid.feedback ? `Feedback: ${bid.feedback}` : '',
  ].filter(Boolean).join(' ');

  return (
    <HideValuesContext.Provider value={!!view.hideValues}>
    <div className="p-4 md:p-5">
      <div className="text-[12px] font-semibold text-gray-500 mb-2 flex flex-wrap items-center gap-1.5">
        <button onClick={() => navigate('bid-tracker')} className="hover:text-navy">← Pipeline Tracker</button>
        {lead && <><span className="text-gray-300">/</span><span>Leads</span></>}
        <span className="text-gray-300">/</span><span>{bid.status}</span>
        <span className="text-gray-300">/</span><span className="font-mono text-gold">{bid.reference || `#${bid.id}`}</span>
      </div>
      {readOnly && (
        <div role="status" className="flex items-center gap-2 flex-wrap bg-amber-50 border border-amber-200 text-amber-800 text-[12px] rounded px-3 py-2 mb-3">
          <span>
            This bid was merged into{' '}
            <button onClick={() => navigate('bid-detail', bid.merged_into.id)} className="font-mono font-bold underline hover:no-underline">{bid.merged_into.reference || `#${bid.merged_into.id}`}</button>
            {bid.merged_into.opportunity ? ` (${bid.merged_into.opportunity})` : ''}
            {bid.merged_at ? ` on ${fmtDateLong(bid.merged_at)}` : ''}. It is kept for its history and cannot be changed here.
          </span>
        </div>
      )}

      {/* Header card */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-3 flex items-start justify-between gap-3 flex-wrap" data-tour="bid-header">
        <div className="min-w-0 flex-1 basis-[320px]">
          <div className="flex items-center gap-1.5 flex-wrap">
            {bid.reference && <span className="font-mono text-[13px] font-bold text-gold">{bid.reference}</span>}
            {lead && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-50 text-blue-700" title="Not a formal tender: kept out of the tender pipeline figures">Lead</span>}
            <Pill text={bid.status || 'Evaluating'} palette={STATUS_COLORS} />
            {lead && <FollowUpBadge b={bid} />}
            {urg && urg.key !== 'ok' ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: urg.bg, color: urg.color }}>{urg.label}</span> : null}
            <GoneQuietBadge b={bid} />
            <LongWaitBadge b={bid} />
          </div>
          {mergedFrom.length > 0 && (
            <div className="text-[11px] text-gray-500 mt-0.5" title="References of bids merged into this one">
              Also known as {mergedFrom.map((m, i) => (
                <span key={m.id}>{i > 0 ? ', ' : ''}<button onClick={() => navigate('bid-detail', m.id)} className="font-mono font-semibold text-gray-600 hover:text-navy hover:underline">{m.reference || `#${m.id}`}</button></span>
              ))}
            </div>
          )}
          <h1 className="text-[20px] font-extrabold text-navy leading-tight mt-1">{bid.opportunity}</h1>
          <div className="text-[12px] text-gray-500 mt-0.5">
            {bid.client_name ? <span className="font-semibold text-gray-700">{bid.client_name}</span> : bid.buyer ? <span title="Buyer text; link the client under Edit">{bid.buyer}</span> : 'No client'}
            {lead && bid.source ? ` · ${bid.source}` : ''}{bid.sector ? ` · ${bid.sector}` : ''}{ownerName ? ` · ${ownerName}` : ''}
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2.5 text-[12.5px]">
            <span><span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-1">Value ex VAT</span><ValueCell b={bid} size={14} />
              {bid.value_gbp != null && <span className="text-gray-400"> {bid.value_basis === 'framework_ceiling' ? 'framework ceiling' : 'contract value'}, {bid.value_confidence === 'verified' ? 'verified' : 'estimated'}</span>}</span>
            {lead ? (
              <span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-1">Next follow-up</span>
                {bid.next_action_due ? fmtDay(bid.next_action_due) : '—'}
              </span>
            ) : (
            <span>
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-1">{submitted ? 'Submitted' : bid.deadline ? 'Deadline' : 'Watch date'}</span>
              {bid.deadline ? fmtDeadlineWithTime(bid.deadline, bid.deadline_time) : bid.watch_date ? fmtDeadlineWithTime(bid.watch_date) : '—'}
              {platform ? <span className="text-gray-400"> on {platform}</span> : null}
            </span>
            )}
            {bid.key_date && /^\d{4}-\d{2}-\d{2}$/.test(bid.key_date) && (
              <span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-1">{bid.key_date_label || 'Key date'}</span>
                {fmtDay(bid.key_date)}
              </span>
            )}
          </div>
        </div>
        {!readOnly && (
          <div className="flex flex-col items-end gap-1 min-w-0 max-w-full">
            <div className="flex gap-1.5 flex-wrap justify-end relative" data-more-menu>
              {!lead && <button onClick={checkedPortal} disabled={checking} className={btnSm} title="Records that you looked at the buyer's portal today and found nothing new">{checking ? 'Saving…' : 'Checked portal today'}</button>}
              {decided
                ? <button onClick={reopen} disabled={movingStage} className={btnSm} title="Move it back into the workflow, for a decision recorded in error">↩ Reopen to {bid.previous_status || 'Evaluating'}</button>
                : <button onClick={() => setOutcome('open')} className={btnSm}>Record outcome</button>}
              <button onClick={() => setEdit(true)} className={btnNavy}>Edit</button>
              <button onClick={() => setMoreOpen(o => !o)} aria-expanded={moreOpen} className={btnSm}>More ▾</button>
              {moreOpen && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-md shadow-lg min-w-[180px] py-1">
                  {lead && <button onClick={() => { setMoreOpen(false); setConverting(true); }} className="block w-full text-left px-3 py-1.5 text-[12px] text-gray-700 hover:bg-gray-50">Convert to tender…</button>}
                  <button onClick={() => { setMoreOpen(false); setMerging(true); }} className="block w-full text-left px-3 py-1.5 text-[12px] text-gray-700 hover:bg-gray-50">Merge a duplicate…</button>
                  {isAdmin && <button onClick={remove} className="block w-full text-left px-3 py-1.5 text-[12px] text-red-600 hover:bg-red-50">Delete {noun}</button>}
                </div>
              )}
            </div>
            {checkErr && <div role="alert" className="text-[11px] text-red-600">{checkErr}</div>}
          </div>
        )}
      </div>

      {/* Stepper: Upcoming to Won. Lost, No-bid and Park sit beside it (a
          lead has no No-bid: it is not a tender to decline). */}
      <div data-tour="bid-stepper">
        <div className="flex gap-1" lang="en-GB">
          {steps.map((s, i) => {
            const current = s === bid.status, passed = stepIdx !== -1 && i < stepIdx;
            return (
              <button key={s} type="button" onClick={() => step(s)} disabled={readOnly || movingStage} aria-current={current ? 'step' : undefined}
                style={{ hyphens: 'auto' }}
                className={`flex-1 basis-0 min-w-0 px-0.5 py-2 text-[9px] min-[640px]:text-[11px] font-bold rounded-md border leading-tight break-words disabled:cursor-default ${
                  current ? 'bg-navy text-white border-navy' : passed ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-navy border-gray-200 hover:border-gold'}`}>
                {current && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: '#E5B87A' }} />}{s}
              </button>
            );
          })}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            {decided ? <span className="text-[11px] text-gray-500">Decided: <Pill text={bid.status} palette={STATUS_COLORS} /></span> : (
              <>
                <span className="text-[11px] text-gray-400">Close it:</span>
                <button onClick={() => setOutcome('Lost')} disabled={movingStage} className={btnSm}>Lost</button>
                {!lead && <button onClick={() => setOutcome('No-bid')} disabled={movingStage} className={btnSm}>No-bid</button>}
                <button onClick={() => moveTo('Parked', `Park this ${noun}? You can reopen it later if needed.`)} disabled={movingStage} className={btnSm}>Park</button>
              </>
            )}
            {['Evaluating', 'Responding'].includes(bid.status) && requirements.progress.total > 0 && requirements.progress.todo > 0 && (
              <span className="text-[11px] text-amber-700">{requirements.progress.todo} of {requirements.progress.total} compliance matrix items still to do. Responded is refused until they are ticked or waived.</span>
            )}
            {bid.status === 'Awaiting decision' && !bid.attention?.long_wait && <span className="text-[11px] text-gray-500">Waiting on the buyer. A chase is asked for monthly; log it as a Chase.{bid.awaiting_chased_at ? ` Last chased ${fmtDay(bid.awaiting_chased_at)}.` : ''}</span>}
          </div>
        )}
        {/* Six months on with no decision, the bid is most likely lost or
            abandoned: say so beside the buttons that close it. */}
        {!readOnly && bid.attention?.long_wait && (
          <div role="status" className="mt-2 text-[11.5px] bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
            Submitted {bid.deadline ? fmtDay(bid.deadline) : 'more than six months ago'} and still no decision. If the buyer has gone quiet, close it: <b>Lost</b> if it went elsewhere, <b>Park</b> if you are not sure.
          </div>
        )}
      </div>

      {moveErr && <div role="alert" className="mt-2 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded px-2.5 py-1.5">{moveErr}</div>}

      {/* Stage line: navy in both themes. The summary sits in the page and
          scrolls away; only the next action, or the outcome once decided, is
          pinned, so a long summary never covers the record. The pinned strip
          is a sibling rather than a child so it sticks for the whole page, and
          its negative top matches the .content padding (16px, 10px at 768px
          and below) so the record does not show through above it. */}
      <div data-tour="bid-stage-line" className="rounded-t-xl px-4 pt-3.5 pb-1 mt-3" style={{ background: 'var(--navy)', color: '#E2E8F0' }}>
        <Summary bid={bid} readOnly={readOnly} onSaved={loadBid} inBanner />
      </div>
      <div className="sticky -top-[10px] min-[769px]:-top-4 z-10 max-h-[60vh] overflow-y-auto rounded-b-xl px-4 pt-2 pb-3.5 mb-3" style={{ background: 'var(--navy)', color: '#E2E8F0' }}>
        {decided
          ? <div className="text-[12.5px]"><span className="text-[10px] font-bold uppercase tracking-wider mr-1.5" style={{ color: '#E5B87A' }}>Outcome</span>{outcomeLine}</div>
          : <NextAction bid={bid} people={people} readOnly={readOnly} onSaved={refresh} inBanner />}
      </div>

      {/* Two columns that wrap to one on a narrow pane: the main column takes
          the room, the side column keeps at least 300px. */}
      <div className="flex flex-wrap gap-3 items-start">
        {/* Main column */}
        <div className="bg-white border border-gray-200 rounded-xl" style={{ flex: '999 1 480px', minWidth: 0 }}>
          <div className="flex flex-wrap gap-0.5 border-b border-gray-200 px-2" role="tablist">
            {RECORD_TABS.filter(t => (t.key !== 'surveys' || canAccess('surveys')) && !(lead && t.key === 'matrix')).map(t => (
              <button key={t.key} type="button" role="tab" aria-selected={recordTab === t.key} onClick={() => setRecordTab(t.key)} data-tour={`record-tab-${t.key}`}
                className={`px-3 py-2.5 text-[12.5px] font-bold border-b-2 -mb-px ${recordTab === t.key ? 'text-navy border-gold' : 'text-gray-500 border-transparent hover:text-navy'}`}>
                {t.label}{t.key === 'matrix' && requirements.progress.total > 0 ? ` (${requirements.progress.done + requirements.progress.waived}/${requirements.progress.total})` : ''}
              </button>
            ))}
          </div>
          <div className="p-3.5">
            {recordTab === 'timeline' && (
              <div className="space-y-3">
                {!readOnly && <LogComposer bid={bid} people={people} contacts={contacts} onLogged={refresh} />}
                {/* Comments and actions (the Actions tab until 25 October 2026),
                    folded so the log composer above stays the one place to write.
                    A deep link to an action opens it. */}
                <div className="border border-gray-200 rounded-lg" data-tour="record-actions">
                  <button type="button" onClick={() => setActionsOpen(o => !o)} aria-expanded={actionsOpen}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left">
                    <span className="text-[10.5px] font-bold text-gray-500 uppercase tracking-wider">Comments and actions</span>
                    <span className="text-[11px] font-semibold text-navy">{actionsOpen ? 'Hide ▴' : 'Comment, or give someone an action ▾'}</span>
                  </button>
                  {actionsOpen && (
                    <div className="px-3 pb-3">
                      <ActionsPanel entityType="bid" entityId={String(bid.id)} people={people} milestones={[]} currentUser={currentUser}
                        initialActionId={targetActionId} onChanged={loadTimeline} />
                    </div>
                  )}
                </div>
                <div className="border border-gray-200 rounded-lg">
                  <div className="px-3 py-1.5 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-[11px] font-bold text-gray-500 uppercase">Timeline</span>
                    <span className="text-[10px] text-gray-400">Log entries, status changes and comments, newest first</span>
                  </div>
                  {timeline.length === 0 ? (
                    <div className="px-3 py-3 text-[12px] text-gray-400 text-center">Nothing logged yet. Record the first call, email or portal message above.</div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {timeline.map(t => <TimelineRow key={t.id} t={t} bidId={bid.id} currentUser={currentUser} isAdmin={isAdmin} readOnly={readOnly} onDeleted={refresh} />)}
                    </div>
                  )}
                </div>
              </div>
            )}
            {recordTab === 'documents' && (
              <div className="space-y-3">
                <div>
                  <div className={h}>SharePoint folder</div>
                  {folder?.webUrl ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <a href={folder.webUrl} target="_blank" rel="noopener noreferrer" className="text-[12px] font-semibold text-blue-700 hover:underline">📁 Open in SharePoint</a>
                      {!readOnly && <button onClick={createFolder} disabled={folderBusy} title="Re-create from the template folder structure"
                        className="text-[11px] text-gray-500 hover:text-navy underline disabled:opacity-50">{folderBusy ? 'Syncing…' : 'Sync structure'}</button>}
                      {folderErr && <div className="text-[11px] text-red-600 w-full">{folderErr}</div>}
                    </div>
                  ) : (
                    <div>
                      {!readOnly && <button onClick={createFolder} disabled={folderBusy} className={btnNavy}>{folderBusy ? 'Creating…' : '📁 Create bid folder'}</button>}
                      {folderErr && <div className="text-[11px] text-red-600 mt-1">{folderErr}</div>}
                      <div className="text-[10px] text-gray-400 mt-1">Creates this bid's folder on the Pipeline Tracker SharePoint site (set it under Admin → System Automation).</div>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className={h + ' mb-0'}>Uploaded to ops</div>
                    {!readOnly && <button onClick={() => setAddingDoc(true)} className="text-[11px] font-semibold text-navy hover:underline">+ Add</button>}
                  </div>
                  {docs.length === 0 ? (
                    <div className="text-[11px] text-gray-400">No documents yet. Add the RFQ, terms, PQQ/ROM response etc.</div>
                  ) : (
                    <BidDocumentList docs={docs} currentUser={currentUser} isAdmin={isAdmin}
                      onDelete={async (doc) => {
                        if (!window.confirm(`Delete "${doc.title}" v${doc.version}? This cannot be undone.`)) return;
                        try { await api.delete(`crm/bids/${detailId}/documents/${doc.id}`); loadDocs(); }
                        catch (e) { window.alert(e?.message || 'Could not delete this document.'); }
                      }} />
                  )}
                </div>
              </div>
            )}
            {recordTab === 'matrix' && !lead && (
              <ComplianceMatrix bidId={bid.id} bid={bid} people={people} readOnly={readOnly} data={requirements} reload={() => { loadRequirements(); loadBid(); }} />
            )}
            {recordTab === 'hardware' && (
              <BidHardware bid={bid} readOnly={readOnly} notice={hardwareNotice} onChanged={loadTimeline} />
            )}
            {recordTab === 'surveys' && <SurveysPanel bid={bid} readOnly={readOnly} />}
          </div>
        </div>

        {/* Side column */}
        <div className="space-y-3" style={{ flex: '1 1 300px', minWidth: 0 }}>
          <div className={card}>
            <div className="flex items-center justify-between mb-1.5">
              <div className={h + ' mb-0'}>Record fields</div>
              {!readOnly && <button onClick={() => setEdit(true)} className="text-[11px] font-semibold text-navy hover:underline">Edit</button>}
            </div>
            <Field label="Client" value={bid.client_name || (bid.buyer ? <span className="text-gray-500" title="Not linked to a client">{bid.buyer}</span> : null)} />
            <div className="flex justify-between gap-2 py-0.5 text-[12px]">
              <span className="text-gray-400">People</span>
              <span className="text-right">
                {contacts.length === 0 ? <span className="text-gray-400">None yet</span> : contacts.map(c => (
                  <div key={c.contact_id} className="text-gray-800">{c.name}{c.role ? <span className="text-gray-500">, {c.role}</span> : null}{c.is_primary ? <span className="ml-1 text-[9px] font-bold uppercase text-gold">primary</span> : null}</div>
                ))}
                {!readOnly && <div><button onClick={() => setEditingContacts(true)} className="text-[11px] font-semibold text-navy hover:underline">{contacts.length ? 'Change' : '+ Add people'}</button></div>}
              </span>
            </div>
            {lead && <Field label="Source" value={bid.source} />}
            <Field label="Sector" value={bid.sector} />
            <Field label="Service line" value={bid.service_line} />
            <Field label="Owner" value={ownerName} />
            {bid.next_action && bid.next_action_owner_id && bid.next_action_owner_id !== bid.owner_id ? <Field label="Next action on" value={personName(bid.next_action_owner_id)} /> : null}
            <Field label="Reviewer" value={personName(bid.reviewer_id)} />
            <Field label="Review due" value={bid.review_due ? fmtDay(bid.review_due) : null} />
            {!lead && <Field label="Deadline" value={bid.deadline ? fmtDeadlineWithTime(bid.deadline, bid.deadline_time) : null} />}
            {!lead && <Field label="Watch date" value={bid.watch_date ? fmtDay(bid.watch_date) : null} />}
            {bid.key_date ? <Field label={bid.key_date_label || 'Key date'} value={/^\d{4}-\d{2}-\d{2}$/.test(bid.key_date) ? fmtDay(bid.key_date) : bid.key_date} /> : null}
            {primary ? <Field label="Primary contact" value={primary.name} /> : null}
            {columns.filter(c => custom[c.key] != null && custom[c.key] !== '').map(c => <Field key={c.key} label={c.label} value={String(custom[c.key])} />)}
            <Field label="Created" value={bid.created_at ? fmtDay(bid.created_at) : null} />
            <Field label="Last touch" value={bid.last_touch_at ? fmtWhen(bid.last_touch_at) : null} />
            {(bid.result || bid.status === 'No-bid') && (
              <div className="mt-1.5 pt-1.5 border-t border-gray-100 text-[12px]">
                {bid.result ? <Pill text={bid.result} palette={RESULT_COLORS} /> : <Pill text="No-bid" palette={STATUS_COLORS} />}
                {bid.result_score ? <span className="text-gray-500"> score {bid.result_score}</span> : null}
                {bid.result_date ? <span className="text-gray-500"> · {fmtDeadlineWithTime(bid.result_date)}</span> : null}
                {bid.no_bid_reason && <div className="text-[11px] text-gray-600 mt-1">Reason: {bid.no_bid_reason}</div>}
                {(bid.winning_supplier || bid.winning_price_gbp != null) && (
                  <div className="text-[11px] text-gray-600 mt-1">Winning bid: {bid.winning_supplier || 'supplier not recorded'}{bid.winning_price_gbp != null ? ` at ${maskMoney(formatValueGbp(bid.winning_price_gbp), !!view.hideValues)}` : ''}</div>
                )}
                {causes.length > 0 && <div className="text-[11px] text-gray-500 mt-1">Root causes: {causes.join(', ')}</div>}
                {bid.feedback && <div className="text-[11px] italic text-gray-600 mt-1">{bid.feedback}</div>}
              </div>
            )}
          </div>

          {/* Platforms: where the notice was seen and where the response goes
              in. A lead has no notice and no portal. */}
          {!lead && <div className={card}>
            <div className="flex items-center justify-between mb-1.5">
              <div className={h + ' mb-0'}>Platforms</div>
              <span className="text-[11px] text-gray-500">{bid.last_checked_at ? `Checked ${fmtDay(bid.last_checked_at)}` : 'Never checked'}</span>
            </div>
            {!Array.isArray(bid.platforms) || bid.platforms.length === 0 ? (
              <div className="text-[11px] text-gray-400">None recorded. Add them under Edit, with the account that holds the login and the buyer's reference.</div>
            ) : (
              <div className="space-y-1.5">
                {bid.platforms.map(p => (
                  <div key={p.id || p.platform} className="text-[11px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-navy">{p.platform}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-600">{USED_FOR_LABEL[p.used_for] || p.used_for}</span>
                      {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">Open ↗</a>}
                    </div>
                    <div className="text-[10px] text-gray-500">
                      {[p.account_holder && `Account: ${p.account_holder}`, p.buyer_reference && `Buyer ref: ${p.buyer_reference}`].filter(Boolean).join(' · ') || <span className="text-red-600">No account holder or buyer reference recorded</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {bid.attention?.portal_due && !readOnly && <div className="text-[11px] text-purple-700 mt-1.5">A week or more since the portal was checked. Use Checked portal today, or log a Portal entry, even to say there was nothing.</div>}
          </div>}
        </div>
      </div>

      {edit && (
        <BidForm existing={bid} columns={columns} people={people} clients={clients}
          onClose={() => setEdit(false)}
          onSaved={() => { setEdit(false); refresh(); }} />
      )}
      {outcome && (
        <OutcomeForm b={bid} target={outcome}
          onClose={() => setOutcome(null)}
          onSaved={(res) => {
            setOutcome(null); refresh();
            // A win carried the hardware (or could not): open the Hardware tab
            // with the result, since that is where the lines live.
            const c = res?.hardware_carry;
            if (c && (c.carried > 0 || c.error)) {
              setHardwareNotice(c.error
                ? { tone: 'warn', text: c.error }
                : { tone: 'ok', text: `${c.carried} hardware line${c.carried === 1 ? '' : 's'} ${c.created ? 'raised' : 'added to'} ${c.sow?.reference} on the Opportunity Tracker.` });
              setRecordTab('hardware');
            }
          }} />
      )}
      {converting && (
        <ConvertToTenderDialog bid={bid} onClose={() => setConverting(false)}
          onConverted={() => { setConverting(false); refresh(); }} />
      )}
      {merging && (
        <BidMergeDialog bid={bid}
          onClose={() => setMerging(false)}
          onMerged={(survivorId) => {
            setMerging(false);
            if (survivorId === bid.id) { refresh(); loadDocs(); }
            else navigate('bid-detail', survivorId);
          }} />
      )}
      {addingDoc && (
        <BidDocumentModal
          onClose={() => setAddingDoc(false)}
          onSave={async (payload) => { await api.post(`crm/bids/${detailId}/documents`, payload); setAddingDoc(false); loadDocs(); }}
        />
      )}
      {editingContacts && (
        <ContactsDialog bid={bid} contacts={contacts} clients={clients}
          onClose={() => setEditingContacts(false)}
          onSaved={() => { setEditingContacts(false); loadBid(); }} />
      )}
    </div>
    </HideValuesContext.Provider>
  );
}

// Convert to tender: a lead whose formal ITT has arrived becomes a bid, with
// its timeline, people, documents and hardware kept. The tender needs the
// date a bid needs (a deadline, or a watch date while the notice has none),
// so the dialog asks for it and the button does the conversion.
function ConvertToTenderDialog({ bid, onClose, onConverted }) {
  const [deadline, setDeadline] = useState('');
  const [time, setTime] = useState('');
  const [watch, setWatch] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const convert = async () => {
    if (!deadline && !watch) { setErr('Give the submission deadline, or a watch date if the notice has none yet.'); return; }
    setSaving(true); setErr('');
    try {
      // The lead's In Discussion is a tender's Evaluating: the ITT is in and
      // the decision to respond is still to make.
      const status = bid.status === 'In Discussion' ? 'Evaluating' : bid.status;
      await api.put(`crm/bids/${bid.id}`, { kind: 'bid', deadline: deadline || null, deadline_time: deadline && time ? time : null, watch_date: watch || null, ...(status !== bid.status ? { status, previous_status: bid.status } : {}) });
      onConverted();
    } catch (e) {
      setErr(e?.message || 'Could not convert this lead.');
    } finally { setSaving(false); }
  };
  return (
    <Modal title="Convert to tender" onClose={onClose}
      footer={<>
        {err && <span role="alert" className="text-[11px] font-semibold text-red-600 mr-auto">{err}</span>}
        <button onClick={onClose} className={btnSm}>Keep it as a lead</button>
        <button onClick={convert} disabled={saving} className={btnNavy}>{saving ? 'Converting…' : 'Convert to tender'}</button>
      </>}>
      <p className="text-[12px] text-gray-600 mb-3">
        {bid.reference} moves from Leads to the tender pipeline, keeping its timeline, people, documents and hardware.
        It then counts in the Pipeline figures and Reports, and takes the compliance matrix and portals.
      </p>
      <label className="block text-[11px] font-bold text-gray-500 mb-1">Submission deadline</label>
      <div className="flex flex-wrap gap-2 mb-2">
        <input type="date" value={deadline} onChange={e => { setDeadline(e.target.value); setErr(''); }} className="form-input text-[12px]" style={{ width: 'auto' }} aria-label="Submission deadline" />
        <input type="time" value={time} onChange={e => setTime(e.target.value)} className="form-input text-[12px]" style={{ width: 'auto' }} title="Closing time, UK clock" aria-label="Closing time" />
      </div>
      <label className="block text-[11px] font-bold text-gray-500 mb-1">or a watch date, if the notice has no deadline yet</label>
      <input type="date" value={watch} onChange={e => { setWatch(e.target.value); setErr(''); }} className="form-input text-[12px]" style={{ width: 'auto' }} aria-label="Watch date" />
    </Modal>
  );
}

// The person picker for a next action: the owner by default, or anyone else.
function WhoSelect({ value, people, ownerId, onChange, className }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} aria-label="Who the next action is on" className={className} style={{ width: 'auto' }}>
      <option value="">On the owner</option>
      {people.filter(p => p.id !== ownerId).map(p => <option key={p.id} value={p.id}>{`On ${p.first_name || ''} ${p.last_name || ''}`.trim()}</option>)}
    </select>
  );
}

// ── Next action, edited in place ─────────────────────────────────────────────
// On the navy stage line: the step, who it is on and when, with Done and
// Change beside it. Done is the everyday update in one go: it logs what was
// done on the timeline and sets the next step in the same write (POST
// interactions with next_action), so the stage line and the timeline cannot
// drift apart. A next action reading as "send" is logged as an email.
function NextAction({ bid, people = [], readOnly, onSaved, inBanner }) {
  const [mode, setMode] = useState(null);   // null | 'edit' | 'done'
  const [text, setText] = useState(bid.next_action || '');
  const [due, setDue] = useState(bid.next_action_due || '');
  const [who, setWho] = useState(bid.next_action_owner_id || '');
  const [done, setDone] = useState('');
  const [kind, setKind] = useState('note');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText(bid.next_action || ''); setDue(bid.next_action_due || ''); setWho(bid.next_action_owner_id || ''); }, [bid.next_action, bid.next_action_due, bid.next_action_owner_id]);
  const nameOf = (id) => { const p = people.find(x => x.id === id); return p ? `${p.first_name || ''} ${p.last_name || ''}`.trim() : ''; };
  const onName = nameOf(actionOwnerId(bid));
  const late = bid.next_action_due && bid.next_action_due < todayIso();
  const close = () => { setMode(null); setErr(''); };
  const startDone = () => {
    setDone(bid.next_action ? `Done: ${bid.next_action}` : '');
    setKind(kindForDone(bid.next_action));
    setText(''); setDue(''); setWho('');
    setErr(''); setMode('done');
  };
  const save = async () => {
    setSaving(true); setErr('');
    try {
      if (mode === 'done') {
        if (!done.trim()) { setErr('Say what was done, in a line.'); return; }
        await api.post(`crm/bids/${bid.id}/interactions`, { kind, summary: done.trim(), occurred_at: todayIso(), next_action: text.trim() || null, next_action_due: due || null, next_action_owner_id: who || null });
      } else {
        await api.put(`crm/bids/${bid.id}`, { next_action: text.trim() || null, next_action_due: due || null, next_action_owner_id: who || null });
      }
      close(); onSaved();
    } catch (e) { setErr(e?.message || 'Could not save.'); }
    finally { setSaving(false); }
  };
  const open = !OUTCOME_STATUSES.includes(bid.status);
  const field = 'form-input text-[12px]';
  const nextFields = (
    <div className="flex items-center gap-2 flex-wrap">
      <input value={text} onChange={e => setText(e.target.value)} maxLength={NEXT_ACTION_MAX} placeholder="The one next step" className={`${field} flex-1 min-w-[200px]`} autoFocus={mode === 'edit'} />
      <input type="date" value={due} onChange={e => setDue(e.target.value)} className={field} style={{ width: 'auto' }} title="By when" aria-label="Next action due" />
      {people.length > 0 && <WhoSelect value={who} people={people} ownerId={bid.owner_id} onChange={setWho} className="form-select text-[12px]" />}
    </div>
  );

  if (inBanner && !mode) {
    return (
      <div className="flex items-start gap-2 flex-wrap" data-tour="bid-next-action">
        <div className="text-[12.5px] font-bold min-w-0 flex-1" style={{ color: '#E5B87A' }}>
          <span className="text-[10px] uppercase tracking-wider mr-1.5">Next</span>
          {bid.next_action
            ? <span>{bid.next_action}{bid.next_action_due ? <span style={late ? { color: '#FCA5A5' } : undefined}> {late ? 'overdue since' : 'by'} {fmtDay(bid.next_action_due)}</span> : ''}{onName ? <span className="font-semibold" style={{ color: '#CBD5E1' }}> · on {onName}</span> : null}</span>
            : <span>No next action set</span>}
        </div>
        {!readOnly && open && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {bid.next_action && <button onClick={startDone} className="px-2.5 py-1 text-[11.5px] font-bold rounded-md" style={{ background: '#E5B87A', color: '#0D1D32' }} title="Log that this is done and set the next step">✓ Done</button>}
            <button onClick={() => { setErr(''); setMode('edit'); }} className="text-[11.5px] font-semibold underline" style={{ color: '#E5B87A' }}>{bid.next_action ? 'Change' : 'Set'}</button>
          </div>
        )}
      </div>
    );
  }
  if (!inBanner && !mode) {
    return (
      <div className="flex justify-between gap-2 py-0.5 text-[12px]">
        <span className="text-gray-400">Next action</span>
        <span className="text-right">
          {bid.next_action
            ? <span className="text-gray-800">{bid.next_action}{bid.next_action_due ? <span className="text-gray-500"> by {fmtDay(bid.next_action_due)}</span> : null}</span>
            : open ? <span className="text-red-600 font-semibold">None set</span> : <span className="text-gray-400">—</span>}
          {!readOnly && open && <button onClick={() => setMode('edit')} className="ml-2 text-[11px] font-semibold text-navy hover:underline">{bid.next_action ? 'Change' : 'Set'}</button>}
        </span>
      </div>
    );
  }
  return (
    <div className={`py-1 text-[12px] ${inBanner ? 'bg-white text-gray-700 rounded-lg p-3' : ''}`}>
      {mode === 'done' ? (
        <>
          <div className={h}>What was done</div>
          <div className="flex items-center gap-2 flex-wrap mb-2.5">
            <select value={kind} onChange={e => setKind(e.target.value)} aria-label="Kind of entry" className="form-select text-[12px]" style={{ width: 'auto' }}>
              {KINDS.map(k => <option key={k.key} value={k.key}>{k.icon} {k.label}</option>)}
            </select>
            <input value={done} onChange={e => setDone(e.target.value)} placeholder="What was done, in a line" className={`${field} flex-1 min-w-[200px]`} autoFocus />
          </div>
          <div className={h}>Then next</div>
          {nextFields}
          <div className="text-[10.5px] text-gray-400 mt-1">Logged on the timeline today. Leave the next step blank if there is none yet; the bid will ask for one.</div>
        </>
      ) : (
        <>
          <div className={h}>Next action</div>
          {nextFields}
        </>
      )}
      <div className="flex items-center gap-2 flex-wrap mt-2.5">
        <button onClick={save} disabled={saving} className={btnNavy}>{saving ? 'Saving…' : mode === 'done' ? 'Log it and set next' : 'Save'}</button>
        <button onClick={close} className={btnSm}>Cancel</button>
        {err && <span role="alert" className="text-[11.5px] text-red-600">{err}</span>}
      </div>
    </div>
  );
}

// ── The standing summary ─────────────────────────────────────────────────────
// A line or two (300 characters, src/api/bidStreamline.js): what the bid is
// and what would win it. Dated events go on the timeline.
function Summary({ bid, readOnly, onSaved, inBanner }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(bid.summary || '');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText(bid.summary || ''); }, [bid.summary]);
  const save = async () => {
    setSaving(true); setErr('');
    try { await api.put(`crm/bids/${bid.id}`, { summary: text.trim() || null }); setEditing(false); onSaved(); }
    catch (e) { setErr(e?.message || 'Could not save.'); }
    finally { setSaving(false); }
  };
  if (inBanner && !editing) {
    return (
      <div className="flex items-start justify-between gap-2">
        <div className="text-[12.5px] leading-relaxed whitespace-pre-wrap min-w-0">{bid.summary || <span style={{ opacity: 0.7 }}>No summary yet: the standing picture of what this is and what would win it.</span>}</div>
        {!readOnly && <button onClick={() => setEditing(true)} className="text-[11.5px] font-semibold underline flex-shrink-0" style={{ color: '#E5B87A' }}>{bid.summary ? 'Edit' : '+ Write'}</button>}
      </div>
    );
  }
  const over = text.length > SUMMARY_MAX;
  return (
    <div className={inBanner ? 'bg-white text-gray-700 rounded-lg p-3' : card}>
      <div className="flex items-center justify-between mb-1">
        <div className={h + ' mb-0'}>Summary</div>
        {!readOnly && !editing && <button onClick={() => setEditing(true)} className="text-[11px] font-semibold text-navy hover:underline">{bid.summary ? 'Edit' : '+ Write'}</button>}
      </div>
      {editing ? (
        <div>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={3} className="form-input w-full text-[12px]" placeholder="The standing picture in a line or two: what it is, the incumbent, how it is evaluated, what would win it. Dated events belong on the timeline." autoFocus />
          <div className={`text-[10.5px] text-right ${over ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>{text.length} / {SUMMARY_MAX}{over ? ': shorten it, and put the detail on the timeline' : ''}</div>
          <div className="flex items-center gap-2 mt-1.5">
            <button onClick={save} disabled={saving || over} className={btnNavy}>{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={() => { setEditing(false); setText(bid.summary || ''); setErr(''); }} className={btnSm}>Cancel</button>
            {err && <span role="alert" className="text-[11px] text-red-600">{err}</span>}
          </div>
        </div>
      ) : (
        <div className="text-[12px] text-gray-700 whitespace-pre-wrap">{bid.summary || <span className="text-gray-400">No summary yet.</span>}</div>
      )}
    </div>
  );
}

// ── Logging an interaction ───────────────────────────────────────────────────
// One line for what happened, and, beside it, the bid's next step, which the
// same entry can move (POST interactions with next_action). The next step is
// only sent when it was changed here.
function LogComposer({ bid, people = [], contacts, onLogged }) {
  const bidId = bid.id;
  const [kind, setKind] = useState('call');
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState('');
  const [when, setWhen] = useState(todayIso());
  const [contactId, setContactId] = useState('');
  const [more, setMore] = useState(false);
  const [next, setNext] = useState(false);   // the next-step row is open
  const [text, setText] = useState(bid.next_action || '');
  const [due, setDue] = useState(bid.next_action_due || '');
  const [who, setWho] = useState(bid.next_action_owner_id || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const openNext = () => { setText(bid.next_action || ''); setDue(bid.next_action_due || ''); setWho(bid.next_action_owner_id || ''); setNext(true); };
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!summary.trim()) { setErr('Say what happened, in a line.'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { kind, summary: summary.trim(), body: body.trim() || null, occurred_at: when || null, contact_id: contactId || null };
      if (next) Object.assign(payload, { next_action: text.trim() || null, next_action_due: due || null, next_action_owner_id: who || null });
      await api.post(`crm/bids/${bidId}/interactions`, payload);
      setSummary(''); setBody(''); setMore(false); setNext(false);
      onLogged();
    } catch (ex) { setErr(ex?.message || 'Could not log this.'); }
    finally { setSaving(false); }
  };
  const k = kindOf(kind);
  return (
    <form onSubmit={submit} className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-tour="bid-log">
      <div className="flex gap-1 flex-wrap mb-2">
        {KINDS.map(x => (
          <button key={x.key} type="button" onClick={() => setKind(x.key)} aria-pressed={kind === x.key}
            className={`px-2.5 py-0.5 text-[11.5px] font-semibold rounded-full border ${kind === x.key ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:border-gold'}`}>
            {x.icon} {x.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2 items-center flex-wrap">
        <input value={summary} onChange={e => setSummary(e.target.value)} placeholder={`${k.label}: what happened, in a line`} className="form-input flex-1 min-w-[220px] text-[12px]" />
        <input type="date" value={when} onChange={e => setWhen(e.target.value)} className="form-input text-[12px]" style={{ width: 'auto' }} title="When it happened" aria-label="When it happened" />
        {contacts.length > 0 && (
          <select value={contactId} onChange={e => setContactId(e.target.value)} className="form-select text-[12px]" style={{ width: 'auto' }} title="Who it was with">
            <option value="">With…</option>
            {contacts.map(c => <option key={c.contact_id} value={c.contact_id}>{c.name}</option>)}
          </select>
        )}
        <button type="submit" disabled={saving} className={btnNavy}>{saving ? 'Saving…' : 'Log it'}</button>
      </div>
      <div className="text-[10.5px] text-gray-400 mt-1">{k.hint}</div>
      {more && <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder="Detail, if the line is not enough" className="form-input w-full text-[12px] mt-1.5" />}
      {next && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <div className={h}>And the next step</div>
          <div className="flex items-center gap-2 flex-wrap">
            <input value={text} onChange={e => setText(e.target.value)} maxLength={NEXT_ACTION_MAX} placeholder="The one next step" className="form-input flex-1 min-w-[200px] text-[12px]" />
            <input type="date" value={due} onChange={e => setDue(e.target.value)} className="form-input text-[12px]" style={{ width: 'auto' }} title="By when" aria-label="Next step due" />
            {people.length > 0 && <WhoSelect value={who} people={people} ownerId={bid.owner_id} onChange={setWho} className="form-select text-[12px]" />}
          </div>
        </div>
      )}
      <div className="flex gap-3 mt-1.5">
        {!more && <button type="button" onClick={() => setMore(true)} className="text-[10.5px] text-gray-500 hover:text-navy underline">+ add detail</button>}
        {!next && !OUTCOME_STATUSES.includes(bid.status) && <button type="button" onClick={openNext} className="text-[10.5px] text-gray-500 hover:text-navy underline">+ update the next step</button>}
      </div>
      {err && <div role="alert" className="text-[11px] text-red-600 mt-1">{err}</div>}
    </form>
  );
}

function TimelineRow({ t, bidId, currentUser, isAdmin, readOnly, onDeleted }) {
  const [open, setOpen] = useState(false);
  const remove = async () => {
    if (!window.confirm('Remove this log entry?')) return;
    try { await api.delete(`crm/bids/${bidId}/interactions/${t.id}`); onDeleted(); }
    catch (e) { window.alert(e?.message || 'Could not remove it.'); }
  };
  if (t.type === 'status') {
    return (
      <div className="px-3 py-1.5 flex items-start gap-2 text-[11px] bg-gray-50/60">
        <span className="text-[10px] text-gray-400 whitespace-nowrap w-24 flex-shrink-0">{fmtWhen(t.at)}</span>
        <span>
          {t.from_status ? <><Pill text={t.from_status} palette={STATUS_COLORS} /> → </> : <span className="text-gray-400">Created as </span>}
          <Pill text={t.to_status} palette={STATUS_COLORS} />
          {t.person_name ? <span className="text-gray-500"> by {t.person_name}</span> : null}
        </span>
      </div>
    );
  }
  if (t.type === 'comment') {
    return (
      <div className="px-3 py-2 flex items-start gap-2 text-[11px]">
        <span className="text-[10px] text-gray-400 whitespace-nowrap w-24 flex-shrink-0">{fmtWhen(t.at)}</span>
        <div className="min-w-0">
          <span className="text-[10px] font-bold uppercase text-gold mr-1">Comment</span>
          <span className="font-semibold text-navy">{t.person_name || 'Someone'}</span>
          <div className="text-[12px] text-gray-700 whitespace-pre-wrap mt-0.5">{t.summary}</div>
        </div>
      </div>
    );
  }
  const k = kindOf(t.kind);
  const mine = currentUser && t.person_name && personMatches(currentUser, t);
  return (
    <div className="px-3 py-2 flex items-start gap-2 text-[11px]">
      <span className="text-[10px] text-gray-400 whitespace-nowrap w-24 flex-shrink-0">{fmtWhen(t.at)}</span>
      <div className="min-w-0 flex-1">
        <span className="mr-1">{k.icon}</span>
        <span className="text-[10px] font-bold uppercase text-gray-500 mr-1">{k.label}</span>
        <span className="text-[12px] text-gray-800">{t.summary}</span>
        <div className="text-[10px] text-gray-400 mt-0.5">
          {t.person_name || 'Someone'}{t.contact_name ? ` · with ${t.contact_name}` : ''}
          {t.body && <button onClick={() => setOpen(o => !o)} className="ml-2 underline hover:text-navy">{open ? 'less' : 'more'}</button>}
          {!readOnly && (isAdmin || mine) && <button onClick={remove} className="ml-2 text-gray-300 hover:text-red-500">remove</button>}
        </div>
        {open && t.body && <div className="text-[11px] text-gray-600 whitespace-pre-wrap mt-1 border-l-2 border-gray-100 pl-2">{t.body}</div>}
      </div>
    </div>
  );
}
const personMatches = (u, t) => `${u.first_name || ''} ${u.last_name || ''}`.trim() === t.person_name;

// ── The compliance matrix ────────────────────────────────────────────────────
function ComplianceMatrix({ bidId, bid, people, readOnly, data, reload }) {
  const { rows, progress } = data;
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState('');
  const [attach, setAttach] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);
  const [library, setLibrary] = useState([]);
  const [reviewer, setReviewer] = useState(bid.reviewer_id || '');
  const [reviewDue, setReviewDue] = useState(bid.review_due || '');
  const [reviewErr, setReviewErr] = useState('');
  useEffect(() => { setReviewer(bid.reviewer_id || ''); setReviewDue(bid.review_due || ''); }, [bid.reviewer_id, bid.review_due]);
  useEffect(() => { api.get('crm/library').then(d => setLibrary((d?.rows || []).filter(i => i.source === 'library'))).catch(() => {}); }, []);

  const add = async () => {
    if (!q.trim()) { setErr('Name the question or requirement.'); return; }
    setBusy('add'); setErr('');
    try { await api.post(`crm/bids/${bidId}/requirements`, { question: q.trim(), word_limit: limit || null, attachment_required: attach }); setQ(''); setLimit(''); setAttach(false); reload(); }
    catch (e) { setErr(e?.message || 'Could not add it.'); }
    finally { setBusy(null); }
  };
  const update = async (r, patch) => {
    setBusy(r.id); setErr('');
    try { await api.put(`crm/bids/${bidId}/requirements/${r.id}`, patch); reload(); }
    catch (e) { setErr(e?.message || 'Could not save.'); }
    finally { setBusy(null); }
  };
  const waive = (r) => {
    const note = window.prompt(`Why is "${r.question}" not needed?`, r.waiver_note || '');
    if (note === null) return;
    if (!note.trim()) { setErr('A waiver needs a reason.'); return; }
    update(r, { status: 'waived', waiver_note: note.trim() });
  };
  const remove = async (r) => {
    if (!window.confirm(`Remove "${r.question}" from the matrix?`)) return;
    setBusy(r.id);
    try { await api.delete(`crm/bids/${bidId}/requirements/${r.id}`); reload(); }
    catch (e) { setErr(e?.message || 'Could not remove it.'); }
    finally { setBusy(null); }
  };
  const saveReview = async () => {
    setReviewErr('');
    try { await api.put(`crm/bids/${bidId}`, { reviewer_id: reviewer || null, review_due: reviewDue || null }); reload(); }
    catch (e) { setReviewErr(e?.message || 'Could not save.'); }
  };
  const submitted = ['Responded', 'Awaiting decision', 'In Discussion', 'Won', 'Lost'].includes(bid.status);
  return (
    <div className={card}>
      <div className="flex items-center justify-between mb-1">
        <div className={h + ' mb-0'}>Compliance matrix</div>
        <span className={`text-[11px] font-semibold ${progress.total && !progress.todo ? 'text-emerald-700' : 'text-gray-500'}`}>
          {progress.total ? `${progress.done + progress.waived} of ${progress.total} complete` : 'Empty'}
        </span>
      </div>
      <div className="text-[10px] text-gray-400 mb-1.5">Every question in the ITT with its word count and attachments, ticked off before submission. {submitted ? '' : 'Responded is refused while any row is still to do.'}</div>
      {rows.length > 0 && (
        <table className="w-full text-[11px]">
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-gray-100 align-top">
                <td className="py-1 pr-1 w-5">
                  {!readOnly ? (
                    <button onClick={() => update(r, { status: r.status === 'done' ? 'todo' : 'done' })} disabled={busy === r.id}
                      title={r.status === 'done' ? 'Mark as still to do' : 'Mark done'}
                      className={`w-4 h-4 rounded border text-[11px] leading-none ${r.status === 'done' ? 'bg-emerald-600 border-emerald-600 text-white' : r.status === 'waived' ? 'bg-gray-100 border-gray-300 text-gray-400' : 'bg-white border-gray-300'}`}>
                      {r.status === 'done' ? '✓' : r.status === 'waived' ? '–' : ''}
                    </button>
                  ) : <span>{r.status === 'done' ? '✓' : r.status === 'waived' ? '–' : '○'}</span>}
                </td>
                <td className="py-1 pr-2">
                  <div className={`${r.status === 'todo' ? 'text-gray-800' : 'text-gray-500'}`}>{r.question}</div>
                  <div className="text-[10px] text-gray-400">
                    {[r.word_limit != null && `${r.word_limit.toLocaleString('en-GB')} words`, r.attachment_required ? 'attachment' : null, r.status === 'waived' && `waived: ${r.waiver_note}`, r.library_item_title && `from library: ${r.library_item_title}`].filter(Boolean).join(' · ')}
                  </div>
                </td>
                {!readOnly && (
                  <td className="py-1 text-right whitespace-nowrap">
                    {library.length > 0 && (
                      <select value={r.library_item_id || ''} onChange={e => update(r, { library_item_id: e.target.value || null })} className="form-select text-[10px] mr-1" title="The library item used to answer it">
                        <option value="">Library…</option>
                        {library.map(i => <option key={i.id} value={i.id}>{i.title}</option>)}
                      </select>
                    )}
                    {r.status !== 'waived' && <button onClick={() => waive(r)} disabled={busy === r.id} className="text-[10px] text-gray-400 hover:text-navy underline mr-1">waive</button>}
                    <button onClick={() => remove(r)} disabled={busy === r.id} className="text-[10px] text-gray-300 hover:text-red-500">✕</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!readOnly && (adding ? (
        <div className="mt-2 border-t border-gray-100 pt-2">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Question or requirement, e.g. Q3 Health and safety policy" className="form-input w-full text-[11px] mb-1" autoFocus />
          <div className="flex items-center gap-2 flex-wrap">
            <input type="number" min="0" value={limit} onChange={e => setLimit(e.target.value)} placeholder="Word limit" className="form-input w-24 text-[11px]" />
            <label className="text-[11px] text-gray-600 flex items-center gap-1"><input type="checkbox" checked={attach} onChange={e => setAttach(e.target.checked)} /> attachment required</label>
            <button onClick={add} disabled={busy === 'add'} className={btnNavy}>Add</button>
            <button onClick={() => { setAdding(false); setErr(''); }} className={btnSm}>Done</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="text-[11px] font-semibold text-navy hover:underline mt-1.5">+ Add a requirement</button>
      ))}
      {err && <div role="alert" className="text-[11px] text-red-600 mt-1">{err}</div>}
      {/* One named reviewer and a date before submission: the colour-team
          review cut down to what our volume needs. */}
      <div className="mt-2 border-t border-gray-100 pt-2 text-[11px]">
        <div className="text-[10px] font-bold text-gray-400 uppercase mb-1">Review before submission</div>
        {readOnly ? (
          <div className="text-gray-600">{bid.reviewer_id ? `${(people.find(p => p.id === bid.reviewer_id) || {}).first_name || ''} ${(people.find(p => p.id === bid.reviewer_id) || {}).last_name || ''}`.trim() : 'No reviewer named'}{bid.review_due ? ` by ${fmtDay(bid.review_due)}` : ''}</div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <select value={reviewer} onChange={e => setReviewer(e.target.value)} className="form-select text-[11px]">
              <option value="">Reviewer…</option>
              {people.map(p => <option key={p.id} value={p.id}>{`${p.first_name || ''} ${p.last_name || ''}`.trim()}</option>)}
            </select>
            <input type="date" value={reviewDue} onChange={e => setReviewDue(e.target.value)} className="form-input text-[11px]" title="Review by" />
            {(reviewer !== (bid.reviewer_id || '') || reviewDue !== (bid.review_due || '')) && <button onClick={saveReview} className={btnSm}>Save</button>}
            {reviewErr && <span role="alert" className="text-red-600">{reviewErr}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── The people on the bid ────────────────────────────────────────────────────
function ContactsDialog({ bid, contacts, clients, onClose, onSaved }) {
  const [rows, setRows] = useState(() => contacts.map(c => ({ contact_id: c.contact_id, name: c.name, role: c.role || '', is_primary: !!c.is_primary })));
  const [all, setAll] = useState([]);
  const [q, setQ] = useState('');
  const [clientId, setClientId] = useState(bid.client_id || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [newC, setNewC] = useState({ first_name: '', last_name: '', email: '', role: '' });
  // A person's role on a bid, from the bid_contact_role picklist (Admin → Config Options).
  const [roleOpts, setRoleOpts] = useState([]);
  useEffect(() => {
    api.get('config-options', { category: 'bid_contact_role' })
      .then(d => setRoleOpts((Array.isArray(d) ? d : (d?.rows || [])).map(o => o.value).filter(Boolean)))
      .catch(() => setRoleOpts([]));
  }, []);
  useEffect(() => { api.get('crm/contacts', clientId ? { client_id: clientId } : {}).then(d => setAll(Array.isArray(d) ? d : [])).catch(() => setAll([])); }, [clientId]);
  const chosen = new Set(rows.map(r => r.contact_id));
  const candidates = all.filter(c => !chosen.has(c.id) && (!q || `${c.first_name} ${c.last_name} ${c.email || ''} ${c.client_name || ''}`.toLowerCase().includes(q.toLowerCase()))).slice(0, 12);
  const addRow = (c) => setRows(r => [...r, { contact_id: c.id, name: `${c.first_name} ${c.last_name}`, role: c.job_title || c.role || '', is_primary: r.length === 0 }]);
  const setPrimary = (id) => setRows(r => r.map(x => ({ ...x, is_primary: x.contact_id === id })));
  const createContact = async () => {
    if (!clientId) { setErr('Pick the client first: a contact belongs to an organisation.'); return; }
    if (!newC.first_name.trim() || !newC.last_name.trim()) { setErr('First and last name are needed.'); return; }
    setSaving(true); setErr('');
    try {
      const c = await api.post('crm/contacts', { ...newC, client_id: clientId });
      addRow(c); setCreating(false); setNewC({ first_name: '', last_name: '', email: '', role: '' });
    } catch (e) { setErr(e?.message || 'Could not add the contact.'); }
    finally { setSaving(false); }
  };
  const save = async () => {
    setSaving(true); setErr('');
    try {
      if ((clientId || null) !== (bid.client_id || null)) await api.put(`crm/bids/${bid.id}`, { client_id: clientId || null });
      await api.put(`crm/bids/${bid.id}/contacts`, { contacts: rows.map(r => ({ contact_id: r.contact_id, role: r.role || null, is_primary: r.is_primary })) });
      onSaved();
    } catch (e) { setErr(e?.message || 'Could not save.'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="People on this bid" onClose={onClose}
      footer={<>
        {err && <span role="alert" className="text-[11px] text-red-600 mr-auto">{err}</span>}
        <button onClick={onClose} className="px-3 py-1 text-[12px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={save} disabled={saving} className="px-3 py-1 text-[12px] font-semibold bg-navy text-white rounded disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
      </>}>
      <div className="space-y-2 text-[12px]">
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">Client</label>
          <select value={clientId} onChange={e => setClientId(e.target.value)} className="form-select w-full text-[11px]">
            <option value="">Not linked yet</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">On the bid</label>
          {rows.length === 0 ? <div className="text-gray-400">Nobody yet.</div> : rows.map(r => (
            <div key={r.contact_id} className="flex items-center gap-2 py-0.5">
              <input type="radio" name="primary" checked={r.is_primary} onChange={() => setPrimary(r.contact_id)} title="Primary contact" />
              <span className="font-semibold text-gray-800 flex-1">{r.name}</span>
              <RolePicker value={r.role} options={roleOpts} onChange={v => setRows(list => list.map(x => x.contact_id === r.contact_id ? { ...x, role: v } : x))} />
              <button onClick={() => setRows(list => list.filter(x => x.contact_id !== r.contact_id))} className="text-gray-300 hover:text-red-500">✕</button>
            </div>
          ))}
          <div className="text-[10px] text-gray-400">The radio marks the primary contact, shown on the pipeline row.</div>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">Add someone</label>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={clientId ? 'Search this client\'s contacts' : 'Search all contacts'} className="form-input w-full text-[11px] mb-1" />
          {candidates.map(c => (
            <button key={c.id} onClick={() => addRow(c)} className="block w-full text-left px-2 py-1 text-[11px] rounded hover:bg-gray-50">
              <span className="font-semibold">{c.first_name} {c.last_name}</span><span className="text-gray-500"> · {c.job_title || c.role || ''}{c.client_name ? ` · ${c.client_name}` : ''}</span>
            </button>
          ))}
          {creating ? (
            <div className="border border-gray-100 rounded p-2 mt-1 grid grid-cols-2 gap-1">
              <input value={newC.first_name} onChange={e => setNewC(p => ({ ...p, first_name: e.target.value }))} placeholder="First name" className="form-input text-[11px]" />
              <input value={newC.last_name} onChange={e => setNewC(p => ({ ...p, last_name: e.target.value }))} placeholder="Last name" className="form-input text-[11px]" />
              <input value={newC.email} onChange={e => setNewC(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="form-input text-[11px]" />
              <input value={newC.role} onChange={e => setNewC(p => ({ ...p, role: e.target.value }))} placeholder="Job title" className="form-input text-[11px]" />
              <div className="col-span-2 flex gap-2">
                <button onClick={createContact} disabled={saving} className={btnNavy}>Add contact</button>
                <button onClick={() => setCreating(false)} className={btnSm}>Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setCreating(true)} className="text-[11px] font-semibold text-navy hover:underline mt-1">+ New contact{clientId ? '' : ' (pick the client first)'}</button>
          )}
        </div>
      </div>
    </Modal>
  );
}

// Grouped by title: the latest version shown expanded, older versions
// collapsed into a "v2, v1..." history line so a reissued RFQ or a
// resubmitted PQQ response reads as one document's timeline, not clutter.
// The role on a bid as a picklist. A role that is not on the list (written
// before the list existed, or typed under Other) shows as Other with its text.
function RolePicker({ value, options, onChange }) {
  const listed = !value || options.includes(value);
  const [other, setOther] = useState(!listed);
  useEffect(() => { if (value && options.length && !options.includes(value)) setOther(true); }, [value, options]);
  const choices = options.includes('Other') ? options : [...options, 'Other'];
  return (
    <span className="inline-flex gap-1 items-center">
      <select value={other ? 'Other' : (value || '')} onChange={e => { const v = e.target.value; if (v === 'Other') { setOther(true); onChange(''); } else { setOther(false); onChange(v); } }}
        className="form-select text-[11px]" style={{ width: 'auto' }} aria-label="Role on this bid">
        <option value="">Role on this bid…</option>
        {choices.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {other && <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="Their role" className="form-input text-[11px]" style={{ width: 120 }} />}
    </span>
  );
}

function BidDocumentList({ docs, currentUser, isAdmin, onDelete }) {
  const [openGroup, setOpenGroup] = useState(null);
  const groups = [];
  const byTitle = new Map();
  for (const d of docs) {
    const key = d.title.toLowerCase();
    if (!byTitle.has(key)) { byTitle.set(key, { title: d.title, versions: [] }); groups.push(byTitle.get(key)); }
    byTitle.get(key).versions.push(d);
  }
  for (const g of groups) g.versions.sort((a, b) => b.version - a.version);
  const canDelete = (v) => isAdmin || (currentUser && v.uploaded_by === currentUser.id);

  return (
    <div className="space-y-2">
      {groups.map(g => {
        const latest = g.versions[0];
        const older = g.versions.slice(1);
        return (
          <div key={g.title} className="border border-gray-100 rounded p-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <a href={latest.url} target="_blank" rel="noopener noreferrer" className="text-[12px] font-semibold text-blue-700 hover:underline">{latest.title}</a>
                <span className="text-[10px] text-gray-400 ml-1">v{latest.version}</span>
                <div className="text-[10px] text-gray-400">{latest.uploaded_by_name || 'Someone'} · {fmtWhen(latest.uploaded_at)}</div>
                {latest.notes && <div className="text-[10px] text-gray-500 mt-0.5">{latest.notes}</div>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {older.length > 0 && (
                  <button onClick={() => setOpenGroup(o => o === g.title ? null : g.title)} className="text-[10px] text-gray-400 hover:text-navy">
                    {openGroup === g.title ? 'Hide history' : `${older.length} earlier version${older.length !== 1 ? 's' : ''}`}
                  </button>
                )}
                {canDelete(latest) && (
                  <button onClick={() => onDelete(latest)} title="Delete this version" className="text-gray-300 hover:text-red-500 text-[12px] leading-none">✕</button>
                )}
              </div>
            </div>
            {openGroup === g.title && (
              <div className="mt-1.5 pl-2 border-l border-gray-100 space-y-1">
                {older.map(v => (
                  <div key={v.id} className="flex items-center justify-between gap-2 text-[10px] text-gray-500">
                    <span><a href={v.url} target="_blank" rel="noopener noreferrer" className="hover:underline">v{v.version}</a> — {v.uploaded_by_name || 'Someone'} · {fmtWhen(v.uploaded_at)}</span>
                    {canDelete(v) && <button onClick={() => onDelete(v)} title="Delete this version" className="text-gray-300 hover:text-red-500 leading-none">✕</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BidDocumentModal({ onClose, onSave }) {
  const [title, setTitle] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null); // { url, filename }
  const [typeOpts, setTypeOpts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('config-options', { category: 'document_type' })
      .then(d => setTypeOpts(Array.isArray(d) ? d.filter(o => o.is_active !== 0) : [])).catch(() => {});
  }, []);

  const save = async () => {
    if (!title.trim()) { setErr('Title is required'); return; }
    if (!file) { setErr('Upload a file first'); return; }
    setErr(''); setSaving(true);
    try {
      await onSave({ title: title.trim(), document_type: documentType || null, notes: notes || null, url: file.url, filename: file.filename });
    } catch (e) { setErr(e.message || 'Could not save this document'); } finally { setSaving(false); }
  };

  return (
    <Modal title="Add document" onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-3 py-1 text-[12px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={save} disabled={saving} className="px-3 py-1 text-[12px] font-semibold bg-navy text-white rounded disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
      </>}>
      <div className="space-y-2 text-[12px]">
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">Title *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. RFQ, Terms & Conditions, PQQ Response"
            className="form-input w-full text-[11px]" />
          <div className="text-[9px] text-gray-400 mt-0.5">Uploading again under the same title adds a new version rather than a separate document.</div>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">Type</label>
          <select value={documentType} onChange={e => setDocumentType(e.target.value)} className="form-select w-full text-[11px]">
            <option value="">—</option>
            {typeOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">File *</label>
          <FileUpload onUpload={r => setFile({ url: r.url, filename: r.filename })} onClear={() => setFile(null)} />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="form-input w-full text-[11px]" />
        </div>
        {err && <div className="text-[11px] text-red-600">{err}</div>}
      </div>
    </Modal>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex justify-between gap-2 py-0.5 text-[12px]">
      <span className="text-gray-400 shrink-0">{label}</span>
      <span className="text-gray-800 text-right min-w-0 break-words">{value || '—'}</span>
    </div>
  );
}
